import { t } from 'i18next';
import type { Question } from '@/models/question';
import type { QuestionTypeMapping } from '@/models/questionType';

export const sortQuestions = (questions: Question[]) =>
   [...questions].sort(
      (a, b) => (a?.order ?? Infinity) - (b?.order ?? Infinity),
   );

export const flatMapQuestions = (questions: Question[]): Question[] => {
   return questions.reduce(
      (questionsAcc, question) => [
         ...questionsAcc,
         question,
         ...(question.linked_choices?.reduce(
            (choicesAcc, choice) => [
               ...choicesAcc,
               ...flatMapQuestions(choice?.linked_questions ?? []),
            ],
            [] as Question[],
         ) ?? []),
      ],
      [] as Question[],
   );
};

export const buildFormikPathFromIdPath = (
   idPath: string[],
   pageQuestions: Question[] = [],
): string | null => {
   if (idPath.length === 0) return null;

   const pathParts: string[] = [];
   let currentQuestions = pageQuestions;
   let currentIndex = 0;

   while (currentIndex < idPath.length) {
      const id = String(idPath[currentIndex]);
      const questionIndex = currentQuestions.findIndex(
         (question) => String(question.id) === id,
      );

      if (questionIndex === -1) return null;

      if (pathParts.length === 0) {
         pathParts.push(`questions[${questionIndex}]`);
      } else {
         pathParts.push(`[${questionIndex}]`);
      }

      if (currentIndex === idPath.length - 1) break;

      const question = currentQuestions[questionIndex];
      const nextId = String(idPath[currentIndex + 1]);

      if (question.linked_choices?.length) {
         let foundInChoices = false;

         for (
            let choiceIndex = 0;
            choiceIndex < question.linked_choices.length;
            choiceIndex++
         ) {
            const choice = question.linked_choices[choiceIndex];
            const subQuestions = choice.linked_questions || [];
            const subIndex = subQuestions.findIndex(
               (q) => String(q.id) === nextId,
            );

            if (subIndex !== -1) {
               pathParts.push(
                  `.linked_choices[${choiceIndex}].linked_questions`,
               );
               currentQuestions = subQuestions;
               foundInChoices = true;
               currentIndex++;
               break;
            }
         }

         if (!foundInChoices) return null;
      } else {
         return null;
      }
   }

   return pathParts.join('');
};

export const buildQuestionChoicesRelation = ({
   questionTypeId = '',
   questionTypesMapping,
   previousChoices,
}: {
   questionTypeId?: Question['question_type_id'];
   questionTypesMapping: QuestionTypeMapping;
   previousChoices?: string[];
}) => {
   const isQuestionWithChoices = [
      questionTypesMapping.checkbox,
      questionTypesMapping.radio,
      questionTypesMapping.select,
   ].includes(questionTypeId);

   const isRatingQuestion =
      questionTypeId === questionTypesMapping['rating-star'];

   if (isQuestionWithChoices) {
      return {
         relations: {
            questionChoices: previousChoices
               ? previousChoices.map((choice, index) => ({
                    operation: 'create',
                    attributes: {
                       label: choice,
                       order: index + 1,
                    },
                 }))
               : Array.from({ length: 2 }).map((_, index) => ({
                    operation: 'create',
                    attributes: {
                       label: t('choice.new'),
                       order: index + 1,
                    },
                 })),
         },
      };
   }

   if (isRatingQuestion) {
      const labels = [
         t('defaultRating.one'),
         t('defaultRating.two'),
         t('defaultRating.three'),
         t('defaultRating.four'),
         t('defaultRating.five'),
      ];

      return {
         relations: {
            questionChoices: labels.map((label, index) => ({
               operation: 'create',
               attributes: {
                  label,
                  order: index + 1,
               },
            })),
         },
      };
   }

   return undefined;
};

export const convertIndexToLetters = (zeroBasedIndex: number): string => {
   if (zeroBasedIndex < 0) return '';

   let value = zeroBasedIndex + 1;
   const result: string[] = [];

   while (value > 0) {
      value -= 1;
      const remainder = value % 26;
      const letter = String.fromCharCode(97 + remainder);
      result.unshift(letter);
      value = Math.floor(value / 26);
   }

   return result.join('');
};

export const makeLinkedQuestionLabel = (
   parentLabel: string,
   choiceIndex: number,
   linkedQuestionIndex: number,
   totalLinkedQuestionsForChoice: number,
): string => {
   const choiceLetter = convertIndexToLetters(choiceIndex);

   if (!parentLabel) {
      return totalLinkedQuestionsForChoice === 1
         ? choiceLetter
         : `${choiceLetter}.${convertIndexToLetters(linkedQuestionIndex)}`;
   }

   return totalLinkedQuestionsForChoice === 1
      ? `${parentLabel}.${choiceLetter}`
      : `${parentLabel}.${choiceLetter}.${convertIndexToLetters(linkedQuestionIndex)}`;
};
